import ActivateDeactivateButton from "@app/components/activate-deactivate-button.tsx";
import ActivateDeactivateIndicator from "@app/components/activate-deactivate-indicator.tsx";
import DeleteButton from "@app/components/delete-button.tsx";
import { InvoiceModel } from "@app/data/engine.ts";
import { formatUsd } from "@app/helpers/money-helper";
import { formatDate } from "@app/helpers/time-helper.ts";
import EditIcon from "@mui/icons-material/Edit";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import PrintIcon from "@mui/icons-material/Print";
import { Box } from "@mui/material";
import { Link } from "@tanstack/react-router";
import { createColumnHelper, IconButton } from "bi-lib";

interface Props {
    onPrint: (id: number) => void;
    onUnfinalize: (id: number) => void;
    onDetailsView: (id: number) => void;
    onFinalize: (id: number) => void;
    onDelete: (id: number) => void;
    isAdmin: boolean;
    rowCount: number;
    totalSums?: {
        invoiceAmount?: number;
        timeCostAmount?: number;
        travelExpenseAmount?: number;
        otherExpensesAmount?: number;
    };
}

const columnHelper = createColumnHelper<InvoiceModel>();
export const InvoiceTableDefinition = ({
    onPrint,
    onUnfinalize,
    onDetailsView,
    onFinalize,
    onDelete,
    isAdmin,
    rowCount,
    totalSums
}: Props) => [
        columnHelper.display({
            id: "actions",
            header: "Actions",
            cell: (item) => (
                <Box display={"flex"}>
                    <IconButton tooltip={"Print"} onClick={() => onPrint(item.row.original.invoiceId!)}>
                        <PrintIcon />
                    </IconButton>
                    <Link to={"/invoice/$invoiceId"} params={{ invoiceId: item.row.original.invoiceId!.toString() }}>
                        <IconButton tooltip={"Edit"} color={"info"}>
                            <EditIcon />
                        </IconButton>
                    </Link>
                    {!item.row.original.finalized && (
                        <>
                            <IconButton
                                tooltip={"Invoice Details"}
                                color={"warning"}
                                onClick={() => onDetailsView(item.row.original.invoiceId!)}
                            >
                                <ManageSearchIcon />
                            </IconButton>
                            <DeleteButton onDelete={() => onDelete(item.row.original.invoiceId!)} />
                        </>
                    )}
                </Box>
            ),
            footer: () => `${rowCount} Rows`,
        }),
        columnHelper.accessor((x) => x.invoiceNumber, { id: "invoiceNumber", header: "Invoice Number" }),
        columnHelper.accessor((x) => x.assignmentDescription, {
            id: "assignmentDescription",
            header: "Assignment",
        }),
        columnHelper.accessor((x) => x.startDate, {
            id: "startDate",
            header: "Start",
            cell: (item) => formatDate(item.getValue()),
        }),
        columnHelper.accessor((x) => x.endDate, {
            id: "endDate",
            header: "End",
            cell: (item) => formatDate(item.getValue()),
        }),
        columnHelper.accessor((x) => x.invoiceDate, {
            id: "invoiceDate",
            header: "Invoice Date",
            cell: (item) => formatDate(item.getValue()),
        }),
        columnHelper.accessor((x) => x.invoiceType, { id: "invoiceType", header: "Type" }),
        columnHelper.accessor((x) => x.invoiceAmount, {
            id: "invoiceAmount", header: "Invoice Amount", cell: (item) => formatUsd(item.getValue()),
              footer: () => formatUsd(totalSums?.invoiceAmount), 
        }),
        columnHelper.accessor((x) => x.timeCostAmount, {
            id: "timeCostAmount", header: "Time cost Amount", cell: (item) => formatUsd(item.getValue()),
              footer: () => formatUsd(totalSums?.timeCostAmount), 
        }),
        columnHelper.accessor((x) => x.travelExpenseAmount, {
            id: "travelExpenseAmount", header: "Travel Expenses", cell: (item) => formatUsd(item.getValue()),
              footer: () => formatUsd(totalSums?.travelExpenseAmount), 
        }),
         columnHelper.accessor((x) => x.otherExpensesAmount, {
            id: "otherExpensesAmount", header: "Other Expenses", cell: (item) => formatUsd(item.getValue()),
              footer: () => formatUsd(totalSums?.otherExpensesAmount), 
        }),
        columnHelper.accessor((x) => x.comment, { id: "comment", header: "Comment" }),
        columnHelper.accessor((x) => x.creatorName, { id: "creatorName", header: "Creator" }),
        columnHelper.accessor((x) => x.finalized, {
            id: "finalized",
            header: () => (
                <Box display={"flex"} alignItems={"center"} gap={2}>
                    Finalized
                    <ActivateDeactivateIndicator
                        label={isAdmin ? "Click an item to finalize or unfinalize it" : "Click an item to finalize it"}
                    />
                </Box>
            ),
            cell: (item) => (
                <ActivateDeactivateButton
                    isActive={item.getValue()!}
                    onActivate={() => onFinalize(item.row.original.invoiceId!)}
                    onDeactivate={isAdmin ? () => onUnfinalize(item.row.original.invoiceId!) : undefined}
                    activateLabel={"Finalize"}
                    deactivateLabel={"Unfinalize"}
                />
            ),
        }),
    ];
